/**
 * Small CSV helpers for browser downloads (RFC 4180-style quoting).
 */

import type { VisibilityState } from '@tanstack/react-table';

/** TanStack: `undefined` / missing id means visible; only explicit `false` hides. */
export function isColumnVisibleForCsvExport(columnVisibility: VisibilityState, columnId: string): boolean {
  return columnVisibility[columnId] !== false;
}

/**
 * Export rows using only columns that are visible in the Toggle Columns menu,
 * in `columnIdsInOrder` order.
 */
export function exportCsvWithVisibleColumns<T>(options: {
  rows: T[];
  columnVisibility: VisibilityState;
  columnIdsInOrder: readonly string[];
  columnLabels: Record<string, string>;
  getCellText: (row: T, columnId: string) => string;
  filenameBase: string;
}): void {
  const { rows, columnVisibility, columnIdsInOrder, columnLabels, getCellText, filenameBase } = options;
  const visibleIds = columnIdsInOrder.filter((id) => isColumnVisibleForCsvExport(columnVisibility, id));
  if (visibleIds.length === 0) return;
  const headers = visibleIds.map((id) => columnLabels[id] ?? id);
  const bodyRows = rows.map((row) => visibleIds.map((id) => getCellText(row, id)));
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCsvDocument(`${filenameBase}-${stamp}.csv`, buildCsvDocument(headers, bodyRows));
}

/** Escape a single field for CSV (adds quotes when needed). */
export function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Join one CSV row from already-stringified cells (each cell escaped). */
export function buildCsvRow(cells: string[]): string {
  return cells.map(escapeCsvField).join(',');
}

/** Build full CSV text including header row. UTF-8 BOM prefix improves Excel compatibility. */
export function buildCsvDocument(headerRow: string[], bodyRows: string[][]): string {
  const lines = [buildCsvRow(headerRow), ...bodyRows.map((r) => buildCsvRow(r))];
  return `\ufeff${lines.join('\r\n')}`;
}

/** Trigger a file download in the browser. */
export function downloadCsvDocument(filename: string, csvBody: string): void {
  const blob = new Blob([csvBody], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
